import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Badge,
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    Icon,
    Avatar,
    Box,
    Spinner,
    Switch,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { LuSend } from "react-icons/lu";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaRegEdit, FaUserCheck } from "react-icons/fa";
import { useUser } from "contexts/UserContext";
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Card from "components/card/Card";
import CButton from "components/CButton";
import Pagination from "components/Pagination";
import ConfirmDialog from "components/ConfirmDialog";
import MemberGroupDropdown from "components/MemberGroupDropdown";
const moment = require('moment');

export default function Members({ canCreate, canRead, canUpdate, canDelete }) {
    const { getAllMemebers, sendInvitationToMember, activateMember, updateUserLockStatus, updateUserDiscountLockStatus, deleteUser } = useUser();

    const navigate = useNavigate();

    const textColor = useColorModeValue('navy.700', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    const [globalFilter, setGlobalFilter] = useState('');
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [filter, setFilter] = useState({
        search: '',
        limit: 10,
        page: 1,
        group_id: null
    });

    const handleDelete = async () => {
        if (userToDelete) {
            try {
                await deleteUser(userToDelete);
                setUserToDelete(null);
                fetchUsers();
            } catch (error) {
                console.error("Failed to delete user:", error);
            }
            setIsDialogOpen(false);
        }
    };

    const openDeleteDialog = (userId) => {
        setUserToDelete(userId);
        setIsDialogOpen(true);
    };

    const handleLockChange = async (id, checked) => {
        try {
            const response = await updateUserLockStatus(id, { is_locked: checked })
            if (!response.error) {
                setData((prevData) =>
                    prevData.map((member) =>
                        member.id === id
                            ? {
                                ...member,
                                is_locked: checked,
                            }
                            : member
                    )
                );
            }
        } catch (error) {
            console.error("Failed to change member log status:", error);
        }
    };

    const handleSendInvitationToMember = async (memberId) => {
        try {
            const response = await sendInvitationToMember(memberId);
            if (!response.error) {
                setData((prevData) =>
                    prevData.map((member) =>
                        member.id === memberId
                            ? {
                                ...member,
                                canResendInvite: false,
                                resendInviteAvailableAt: moment().add(5, 'minutes').format('YYYY-MM-DD HH:mm')
                            }
                            : member
                    )
                );
            }
        } catch (error) {
            console.error("Failed to send invitation to member:", error);
        }
    };

    const handleActivateMember = async (memberId) => {
        try {
            const response = await activateMember(memberId);
            if (response && !response.error) {
                setData((prevData) =>
                    prevData.map((member) =>
                        member.id === memberId
                            ? {
                                ...member,
                                is_active: 1,
                                hasJoined: true,
                            }
                            : member
                    )
                );
            }
        } catch (error) {
            console.error("Failed to activate member:", error);
        }
    };

    const handleDiscountLockChange = async (id, checked) => {
        try {
            const response = await updateUserDiscountLockStatus(id, { discount_lock: checked })
            if (!response.error) {
                setData((prevData) =>
                    prevData.map((member) =>
                        member.id === id
                            ? {
                                ...member,
                                discount_lock: checked,
                            }
                            : member
                    )
                );
            }
        } catch (error) {
            console.error("Failed to change member Discount log status:", error);
        }
    };

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => {
        const baseColumns = [columnHelper.accessor('image', {
            header: () => <Text color="gray.400">IMAGE</Text>,
            cell: (info) => {
                const name = info.row.original.first_name || '';
                return (
                    <Flex align="center">
                        <Avatar
                            h="48px"
                            w="48px"
                            bg="#fa8f03"
                            color="white"
                            name={!info.getValue() && name}
                            src={info.getValue() ? info.getValue() : null}
                        />
                    </Flex>
                )
            }
        }),
        columnHelper.accessor('member_id', {
            header: () => <Text color="gray.400">Member ID</Text>,
            cell: (info) => (
                <Text color={textColor} fontSize="md" fontWeight="500">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('group_name', {
            header: () => <Text color="gray.400">GROUP NAME</Text>,
            cell: (info) => (
                <Text color={textColor} fontSize="md" fontWeight="500">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('first_name', {
            header: () => <Text color="gray.400">FIRST NAME</Text>,
            cell: (info) => (
                <Text color={textColor} fontSize="md" fontWeight="500">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('last_name', {
            header: () => <Text color="gray.400">LAST NAME</Text>,
            cell: (info) => (
                <Text color={textColor} fontSize="md" fontWeight="500">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('email', {
            header: () => <Text color="gray.400">EMAIL</Text>,
            cell: (info) => (
                <Text color={textColor} fontSize="md" fontWeight="500">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('dob', {
            header: () => <Text color="gray.400">DATE OF BIRTH</Text>,
            cell: (info) => (
                <Text color={textColor} fontSize="md" fontWeight="500">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('is_locked', {
            header: () => <Text color="gray.400">LOCK</Text>,
            cell: (info) => (
                <Switch
                    colorScheme="orange"
                    isChecked={info.getValue()}
                    onChange={(e) => handleLockChange(info.row.original.id, e.target.checked)}
                />
            ),
        }),
        columnHelper.accessor('discount_lock', {
            header: () => <Text color="gray.400">Discount Lock</Text>,
            cell: (info) => (
                <Switch
                    colorScheme="orange"
                    isChecked={info.getValue()}
                    onChange={(e) => handleDiscountLockChange(info.row.original.id, e.target.checked)}
                />
            ),
        }),
        columnHelper.accessor('is_active', {
            header: () => <Text color="gray.400">STATUS</Text>,
            cell: (info) => (
                <Badge colorScheme={info.getValue() === 1 ? 'green' : 'red'} fontSize="sm" fontWeight="500">
                    {info.getValue() === 1 ? 'Active' : 'Inactive'}
                </Badge>
            ),
        }),
        columnHelper.accessor('access_enabled', {
            header: () => <Text color="gray.400">Kisi Access</Text>,
            cell: (info) => (
                <Badge colorScheme={info.getValue() === 1 ? 'green' : 'red'} fontSize="sm" fontWeight="500">
                    {info.getValue() === 1 ? 'Enabled' : 'Disabled'}
                </Badge>
            ),
        }),
        columnHelper.accessor('actions', {
            header: () => <Text color="gray.400">ACTIONS</Text>,
            cell: (info) => (
                <Flex>
                    {canUpdate && <Icon
                        as={FaRegEdit}
                        h={6}
                        w={6}
                        mt='10px'
                        me='20px'
                        onClick={() => navigate(`/admin/members/edit/${info.row.original.id}`)}
                        cursor='pointer'
                        title="Edit"
                        color='#fa8f03'
                    />}
                    {canDelete && <Icon
                        as={RiDeleteBin6Line}
                        h={6}
                        w={6}
                        mt='10px'
                        me='20px'
                        onClick={() => openDeleteDialog(info.row.original.id)}
                        cursor='pointer'
                        title="Delete"
                        color='#fa8f03'
                    />}
                    {info.row.original.is_active === 0 && (
                        <Icon
                            as={FaUserCheck}
                            h={6}
                            w={6}
                            mt="10px"
                            me="20px"
                            onClick={() => handleActivateMember(info.row.original.id)}
                            cursor='pointer'
                            title={'Activate Member'}
                            color="#fa8f03"
                        />
                    )}
                    {!info.row.original.hasJoined && (
                        <Icon
                            as={LuSend}
                            h={6}
                            w={6}
                            mt="10px"
                            me="20px"
                            onClick={
                                info.row.original.canResendInvite
                                    ? () => handleSendInvitationToMember(info.row.original.id)
                                    : undefined
                            }
                            cursor={info.row.original.canResendInvite ? 'pointer' : 'not-allowed'}
                            opacity={info.row.original.canResendInvite ? 1 : 0.4}
                            title={`${!info.row.original.canResendInvite ? `Enabled at: ${info.row.original.resendInviteAvailableAt}` : 'Send Invitation'}`}
                            color="#fa8f03"
                        />
                    )}
                </Flex>
            ),
        }),
        ]
        return baseColumns;
    }, [canUpdate, canDelete]);

    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const pagination = useMemo(
        () => ({ pageIndex, pageSize }),
        [pageIndex, pageSize],
    );

    const table = useReactTable({
        data,
        columns,
        state: { globalFilter, pagination },
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        fetchUsers();
    }, [filter.search, filter.limit, filter.page, filter.group_id]);

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const data = await getAllMemebers(filter);
            if (data && data.users) {
                setData(data.users);
                setTotalPages(data.totalPages || 0)
            } else {
                console.warn("No members found or data is undefined");
                setData([]);
            }
        } catch (error) {
            console.error("Failed to fetch members:", error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (newPageIndex) => {
        setPagination((prev) => ({
            ...prev,
            pageIndex: newPageIndex,
        }));
        setFilter((prev) => ({
            ...prev,
            page: newPageIndex,
        }));
    };

    return (
        <Flex direction='column' pt={{ sm: "100px", lg: "55px" }}>
            <ConfirmDialog
                position='top'
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onConfirm={handleDelete}
                header="Delete Confirmation"
                message="Are you sure you want to delete this member?"
            />
            <Card px='0px' minHeight="80vh">
                <Flex direction="column" w="100%" overflowX="auto">
                    <Flex align="center" justify="space-between" w="100%" px="22px" mb="36px">
                        <Flex gap='16px'>
                            <DebouncedInput
                                value={globalFilter ?? ''}
                                onChange={(value) => setFilter((prev) => ({ ...prev, search: value, page: 1 }))}
                                placeholder="Search..."
                            />
                            <div className="w-300">
                                <MemberGroupDropdown
                                    label=''
                                    placeholder='member group'
                                    value={filter.group_id}
                                    onChange={(value) => setFilter({ ...filter, group_id: value, page: 1 })}
                                    multiSelect={false}
                                    includeWithoutGroup={true}
                                    className='member-group-dropdown'
                                    labelClassName='member-group-dropdown-label'
                                />
                            </div>
                        </Flex>
                        {canCreate &&
                            <Flex gap='16px'>
                                <CButton onClick={() => navigate('/admin/members/create')}>
                                    Add Member
                                </CButton>
                            </Flex>
                        }
                    </Flex>
                    {isLoading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            borderRadius="10px"
                            overflow="hidden"
                            minHeight="500px"
                        >
                            <Spinner color="#fa8f03" />
                        </Box>
                    ) : (
                        <>
                            <Box border="2px solid #fa8f03" borderRadius="10px" overflow="auto" maxHeight="520px">
                                <Table variant="simple">
                                    <Thead>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <Tr key={headerGroup.id} bg='#fa8f03'>
                                                {headerGroup.headers.map((header) => (
                                                    <Th key={header.id} borderColor='#fa8f03'>
                                                        {header.isPlaceholder ? null : (
                                                            <Flex
                                                                onClick={header.column.getToggleSortingHandler()}
                                                                cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                                                justify="space-between"
                                                                align="center"
                                                                color="white"
                                                            >
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                            </Flex>
                                                        )}
                                                    </Th>
                                                ))}
                                            </Tr>
                                        ))}
                                    </Thead>
                                    <Tbody>
                                        {data.length > 0 ? (
                                            table.getRowModel().rows.map((row) => (
                                                <Tr key={row.id}>
                                                    {row.getVisibleCells().map((cell) => (
                                                        <Td key={cell.id} fontSize="sm" borderColor='#fa8f03'>
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </Td>
                                                    ))}
                                                </Tr>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan={columns.length} textAlign="center">
                                                    No members found.
                                                </Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </Box>
                            <Pagination totalPages={totalPages} currentPage={filter.page} onPageChange={handlePageChange} />
                        </>)}
                </Flex>
            </Card>
        </Flex>
    );
}

function DebouncedInput({ value: initialValue, onChange, debounce = 500, ...props }) {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value);
        }, debounce);
        return () => clearTimeout(timeout);
    }, [value, debounce]);

    return (
        <SearchBar
            value={value}
            onChange={(e) => setValue(e.target.value)}
            h="44px"
            w={{ lg: '390px' }}
            borderRadius="16px"
            {...props}
        />
    );
}
